import {
  AreaListOfSearchModal,
  PrefOfSearchModal,
  CityOfSearchModal,
} from '@doda/common/src/models/MasterCacheModel';

import {
  WardResponse,
  JobCountResponse,
  JobCountDetailResponse,
  AreaResponse,
} from '@doda/api-client/aspida/@types';

/** 表示用エリアのインターフェース */
export interface DisplayArea extends AreaListOfSearchModal {
  /** 都道府県一覧 */
  prefectureList: DisplayPref[];
  /** 求人数 */
  jobCount?: number;
}

/** 表示用都道府県のインターフェース */
export interface DisplayPref extends PrefOfSearchModal {
  /** 求人数 */
  jobCount?: number;
}

/** 表示用市区郡のインターフェース */
export interface DisplayCity extends CityOfSearchModal {
  /** 区一覧 */
  wardList: DisplayWard[];
  /** 求人数 */
  jobCount?: number;
}

/** 表示用区のインターフェース */
export interface DisplayWard extends WardResponse {
  /** ID */
  id: string;
  /** 名前 */
  name: string;
  /** 求人数 */
  jobCount?: number;
}

/**
 * 表示用市区郡一覧作成
 *
 * @param {AreaResponse[]} areaMasterData - エリアマスターデータ
 * @param {JobCountResponse} jobCount - 求人数データ
 * @returns {DisplayCity[]} - 表示用の市区郡一覧
 */
export const createDisplayCity = (
  areaMasterData: AreaResponse[],
  jobCount: JobCountResponse
): DisplayCity[] => {
  const displayCityData: DisplayCity[] = [];

  areaMasterData.forEach((area) => {
    area.prefectureList.forEach((pref) => {
      pref.cityList.forEach((city) => {
        const newCity: DisplayCity = {
          id: String(city.id),
          name: city.name,
          prefId: String(pref.id),
          isOrdinanceDesignatedCity: city.isOrdinanceDesignatedCity,
          jobCount: findJobCount(jobCount.cityList, city.id),
          wardList: [],
        };
        city.wardList.forEach((ward) => {
          newCity.wardList.push({
            id: String(ward.id),
            name: ward.name,
            jobCount: findJobCount(jobCount.wardList, ward.id),
          });
        });
        displayCityData.push(newCity);
      });
    });
  });

  return displayCityData;
};

/**
 * 表示用地域一覧作成
 *
 * @param {AreaResponse[]} areaMasterData - エリアマスターデータ
 * @param {JobCountResponse} jobCount - 求人数データ
 * @returns {DisplayArea[]} - 表示用の地域一覧
 */
export const createDisplayArea = (
  areaMasterData: AreaResponse[],
  jobCount: JobCountResponse
): DisplayArea[] => {
  const displayAreaData: DisplayArea[] = [];

  areaMasterData.forEach((area) => {
    const newArea: DisplayArea = {
      id: String(area.id),
      name: area.name.replace(/^九州$/, '九州・沖縄'),
      jobCount: findJobCount(jobCount.areaList, String(area.id)),
      prefectureList: [],
    };
    area.prefectureList.forEach((pref) => {
      const newPref = {
        id: String(pref.id),
        name: pref.name,
        jobCount: findJobCount(jobCount.prefectureList, String(pref.id)),
      };
      newArea.prefectureList.push(newPref);
    });
    displayAreaData.push(newArea);
  });

  return displayAreaData;
};

/**
 * 求人数を探す
 *
 * @param {JobCountDetailResponse[] | undefined} countList - 求人数リスト
 * @param {string} id - ID
 * @returns {number} - 求人数
 */
export const findJobCount = (
  countList: JobCountDetailResponse[] | undefined,
  id: string
): number => countList?.find((i) => id === i.id)?.count ?? 0;
